import { STATUS_DIMENSION_CONTROL_STAGE, STATUS_LEGEND_LINE } from './safetyCasesWorkspacePresentationCopy';

/**
 * Safety Control Workspace — status dimension taxonomy (UX Slice 1).
 * Every user-visible status maps to exactly one dimension.
 * Wording polish is Slice 5; this module freezes the dimensions.
 */

export const STATUS_DIMENSIONS = [
  'work_state',
  'control_stage',
  'scan_occurrence',
  'scan_workflow',
  'record_lifecycle',
] as const;

export type StatusDimension = (typeof STATUS_DIMENSIONS)[number];

const WORK_STATE_LABELS = new Set([
  'needs review',
  'pending review',
  'review required',
  'unowned',
  'unassigned',
]);

const CONTROL_STAGE_LABELS = new Set([
  'confirmed',
  'monitoring',
  'action assigned',
  'awaiting implementation',
  'awaiting remeasurement',
  'closed',
]);

const SCAN_OCCURRENCE_LABELS = new Set([
  'completed',
  'running',
  'failed',
  'queued',
  'scheduled',
  'deferred',
  'cancelled',
  'missed',
  'skipped (overlap)',
  'no run yet',
  'status unavailable',
]);

const SCAN_WORKFLOW_LABELS = new Set([
  'awaiting sign-off',
  'signed',
  'superseded',
  'insufficient coverage',
  'draft',
  'review required',
]);

const RECORD_LIFECYCLE_LABELS = new Set([
  'signed',
  'superseded',
]);

function normalizeLabel(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Resolve which dimension a status label belongs to.
 * Prefer an explicit hint when the same word appears in multiple dimensions
 * (e.g. "Signed" is scan workflow or record lifecycle; "Completed" is occurrence).
 */
export function resolveStatusDimension(
  label: unknown,
  hint?: StatusDimension | null,
): StatusDimension | null {
  if (hint && (STATUS_DIMENSIONS as readonly string[]).includes(hint)) {
    return hint;
  }
  const key = normalizeLabel(label);
  if (!key) return null;
  if (WORK_STATE_LABELS.has(key)) return 'work_state';
  if (CONTROL_STAGE_LABELS.has(key)) return 'control_stage';
  if (SCAN_OCCURRENCE_LABELS.has(key)) return 'scan_occurrence';
  if (SCAN_WORKFLOW_LABELS.has(key)) return 'scan_workflow';
  if (RECORD_LIFECYCLE_LABELS.has(key)) return 'record_lifecycle';
  return null;
}

export function statusDimensionLabel(dimension: StatusDimension): string {
  switch (dimension) {
    case 'work_state':
      return 'Work state';
    case 'control_stage':
      return STATUS_DIMENSION_CONTROL_STAGE;
    case 'scan_occurrence':
      return 'Scan occurrence';
    case 'scan_workflow':
      return 'Scan workflow';
    case 'record_lifecycle':
      return 'Record lifecycle';
    default:
      return 'Status';
  }
}

/** Preferred display labels for known synonyms (Slice 5). Filter keys stay unchanged. */
const WORK_STATE_DISPLAY_SYNONYMS: Record<string, string> = {
  'pending review': 'Needs review',
  'review required': 'Needs review',
  'needs review': 'Needs review',
  unowned: 'Unowned',
  unassigned: 'Unowned',
};

/**
 * Canonical user-facing status label for workspace display.
 * Does not change filter/matching keys — apply only at render time.
 */
export function canonicalWorkspaceStatusLabel(
  label: unknown,
  hint?: StatusDimension | null,
): string {
  const raw = String(label || '').trim();
  if (!raw) return raw;
  const dimension = resolveStatusDimension(raw, hint);
  const key = normalizeLabel(raw);
  if (dimension === 'work_state' || (!dimension && WORK_STATE_DISPLAY_SYNONYMS[key])) {
    return WORK_STATE_DISPLAY_SYNONYMS[key] || raw;
  }
  return raw;
}

/**
 * One-line status legend for default workspace chrome (once).
 * Customer default chrome exposes at most Work state + Status.
 */
export function workspaceStatusLegendLine(): string {
  return STATUS_LEGEND_LINE;
}

/** True when a register cell is empty / dash placeholder. */
export function isEmptyRegisterValue(value: unknown): boolean {
  const key = normalizeLabel(value);
  return !key || key === '—' || key === '-' || key === 'n/a' || key === 'na';
}

/**
 * Signed records must not show a blank signer.
 * Prefer an explicit signer; otherwise honest System-issued / Legacy labels.
 */
export function formatRecordSignerLabel(input: {
  signedBy?: string | null;
  recordState?: string | null;
  /** When true, treat missing signer as system-issued rather than legacy. */
  systemIssued?: boolean | null;
}): string {
  const signer = String(input.signedBy || '').trim();
  if (signer && !isEmptyRegisterValue(signer)) {
    return signer;
  }
  const state = normalizeLabel(input.recordState);
  if (state !== 'signed' && state !== 'superseded') {
    return '—';
  }
  if (input.systemIssued) {
    return 'System-issued';
  }
  return 'Legacy — signer unavailable';
}

/** Active Safety Cases count labels (site total vs filtered). */
export function formatActiveSafetyCasesCountLabel(input: {
  siteActiveCount: number;
  filteredActiveCount: number;
  filtersActive: boolean;
}): { siteLabel: string; filteredLabel: string | null } {
  const siteLabel = `Active Safety Cases: ${input.siteActiveCount}`;
  if (!input.filtersActive) {
    return { siteLabel, filteredLabel: null };
  }
  return {
    siteLabel,
    filteredLabel: `Active, filtered: ${input.filteredActiveCount}`,
  };
}
